import React, { useState, useEffect } from 'react';
import { AlertTriangle, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const AdBlockerDetector = ({ onAdBlockerDetected, isDarkMode = false, enabled = true }) => {
  const [adBlockerDetected, setAdBlockerDetected] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [dismissCount, setDismissCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setAdBlockerDetected(false);
      return;
    }

    // More accurate detection: Only use the fetch method which is most reliable
    const checkAdBlocker = () => {
      // Create a promise that resolves if ad script loads
      const adScriptTest = new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(true), 2000);
        
        const script = document.createElement('script');
        script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
        script.async = true;
        script.onload = () => {
          clearTimeout(timeout);
          resolve(false); // Ad script loaded = no blocker
        };
        script.onerror = () => {
          clearTimeout(timeout);
          resolve(true); // Script failed = likely blocker
        };
        
        // Don't actually add it to page, just test the loading
        document.head.appendChild(script);
        setTimeout(() => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        }, 2000);
      });

      adScriptTest.then((isBlocked) => {
        if (isBlocked) {
          setAdBlockerDetected(true);
          if (onAdBlockerDetected) {
            onAdBlockerDetected(true);
          }
        } else {
          setAdBlockerDetected(false);
          if (onAdBlockerDetected) {
            onAdBlockerDetected(false);
          }
        }
      });
    };

    // Run check after page loads
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkAdBlocker);
      return () => document.removeEventListener('DOMContentLoaded', checkAdBlocker);
    } else {
      checkAdBlocker();
    }
  }, [enabled, onAdBlockerDetected]);

  const handleDismiss = () => {
    const newCount = dismissCount + 1;
    setDismissCount(newCount);

    // After 3 dismissals, hide the message permanently for this session
    if (newCount >= 3) {
      setIsVisible(false);
      return;
    }

    // First 2 dismissals just hide for 30 seconds
    setIsVisible(false);
    setTimeout(() => {
      setIsVisible(true);
    }, 30000);
  };

  if (!adBlockerDetected || !isVisible || !enabled) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <Card
        className={`m-4 border-2 ${
          isDarkMode
            ? 'bg-gradient-to-r from-red-900/40 to-orange-900/40 border-red-500/50'
            : 'bg-gradient-to-r from-red-50 to-orange-50 border-red-500/50'
        }`}
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={`h-6 w-6 mt-1 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
            />
            <div>
              <CardTitle
                className={`flex items-center gap-2 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}
              >
                <Volume2 className="h-5 w-5" />
                Ad Blocker Detected
              </CardTitle>
              <CardDescription
                className={`mt-2 text-sm ${isDarkMode ? 'text-red-300/80' : 'text-red-700/80'}`}
              >
                We noticed you're using an ad blocker. Please disable it to support our free content.
                Your help keeps this platform running and free for everyone. Thank you!
              </CardDescription>
            </div>
          </div>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="sm"
            className={`ml-2 flex-shrink-0 ${isDarkMode ? 'hover:bg-red-900/50' : 'hover:bg-red-100'}`}
          >
            ✕
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col sm:flex-row gap-2">
            <p
              className={`text-xs ${isDarkMode ? 'text-red-300/70' : 'text-red-600/70'}`}
            >
              Dismiss ({3 - dismissCount} times remaining in this session)
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdBlockerDetector;
